// =============================================================================
// Meeting Module — Data Model
// Alineado con APIBACKNEW (Spring Boot) — ver meeting-frontend-guide.md
// =============================================================================

// -------------------------------------------------------------------
// Enums / Literal types
// -------------------------------------------------------------------

export type MeetingSource = 'manual' | 'booking_page' | 'api' | 'import';
export type MeetingPaymentStatus = 'pending' | 'paid' | 'refunded' | 'failed';

export type SchedulingMode =
  | 'one_on_one'
  | 'round_robin'
  | 'round_robin_weighted'
  | 'collective'
  | 'group_event';

export type EmbedType = 'standalone' | 'inline' | 'popup';

export type ParticipantType = 'organizer' | 'required' | 'optional';
export type ParticipantStatus =
  | 'invited'
  | 'accepted'
  | 'declined'
  | 'tentative'
  | 'attended';

export type ReminderMethod = 'email' | 'inapp' | 'sms' | 'push' | 'all';
export type ReminderTarget = 'organizer' | 'guests' | 'all';
export type BookingReminderMethod = 'email' | 'sms' | 'whatsapp';
export type BookingReminderTarget = 'invitee' | 'host' | 'both';

export type EmailTemplateType =
  | 'confirmation_invitee'
  | 'confirmation_host'
  | 'reminder_invitee'
  | 'reminder_host'
  | 'followup'
  | 'no_show'
  | 'reschedule'
  | 'cancellation';

/**
 * INC 68: opción acotada para los filtros de reuniones (solo entidades
 * realmente involucradas en alguna reunión). email es null salvo en contactos.
 */
export interface MeetingFilterOption {
  id: number;
  name: string;
  email: string | null;
}

/**
 * INC 80 — cuándo envía el sistema cada tipo de plantilla.
 *
 * La pantalla dejaba crear los ocho tipos, pero varios no los leía nadie: el usuario
 * personalizaba un correo que no salía nunca. `cuando` describe el disparador real y
 * `automatico=false` marca los que todavía no tienen envío asociado.
 */
export interface EmailTemplateTypeUsage {
  automatico: boolean;
  cuando: string;
}

export const EMAIL_TEMPLATE_TYPE_USAGE: Record<EmailTemplateType, EmailTemplateTypeUsage> = {
  confirmation_invitee: {
    automatico: true,
    cuando: 'Al crear la reunión, en el correo de confirmación al invitado.',
  },
  confirmation_host: {
    automatico: true,
    cuando: 'Al crear la reunión, en el correo a los participantes internos.',
  },
  reminder_invitee: {
    automatico: true,
    cuando: 'En los recordatorios, si la eliges al configurarlos en la página de reserva.',
  },
  reminder_host: {
    automatico: true,
    cuando: 'En los recordatorios al anfitrión, si la eliges al configurarlos.',
  },
  reschedule: {
    automatico: true,
    cuando: 'Al cambiar la fecha/hora de la reunión (si no existe, se usa la de confirmación).',
  },
  followup: {
    automatico: false,
    cuando: 'Todavía no hay un envío de seguimiento automático.',
  },
  no_show: {
    automatico: false,
    cuando: 'Todavía no hay un envío automático al detectar inasistencia.',
  },
  cancellation: {
    automatico: true,
    cuando:
      'Al cancelar la reunión, en el aviso al invitado (la cancele el equipo o el propio invitado).',
  },
};

/** INC 64: etiquetas en español para el tipo de plantilla (los values técnicos no cambian). */
export const EMAIL_TEMPLATE_TYPE_LABELS: Record<EmailTemplateType, string> = {
  confirmation_invitee: 'Confirmación al invitado',
  confirmation_host: 'Confirmación al anfitrión',
  reminder_invitee: 'Recordatorio al invitado',
  reminder_host: 'Recordatorio al anfitrión',
  followup: 'Seguimiento',
  no_show: 'No asistió',
  reschedule: 'Reprogramación',
  cancellation: 'Cancelación',
};

/**
 * Diccionarios de etiquetas en español del módulo Reuniones.
 * Regla: ninguna tabla, panel o badge pinta la llave técnica; siempre pasa por aquí.
 * Todos los componentes de `pages/meeting/**` deben importar de este archivo.
 */
export function meetingLabel(
  dict: Readonly<Record<string, string>>,
  key: string | null | undefined,
  fallback = '—'
): string {
  if (key === null || key === undefined || key === '') return fallback;
  return dict[key] ?? key;
}

export const MEETING_SOURCE_LABELS: Readonly<Record<string, string>> = {
  manual: 'Manual',
  booking_page: 'Página de reserva',
  api: 'API',
  import: 'Importación',
};

export const MEETING_PAYMENT_STATUS_LABELS: Readonly<Record<string, string>> = {
  pending: 'Pago pendiente',
  paid: 'Pagado',
  refunded: 'Reembolsado',
  failed: 'Pago fallido',
};

export const PARTICIPANT_STATUS_LABELS: Readonly<Record<string, string>> = {
  invited: 'Invitado',
  accepted: 'Aceptó',
  declined: 'Rechazó',
  tentative: 'Tentativo',
  attended: 'Asistió',
};

export const PARTICIPANT_TYPE_LABELS: Readonly<Record<string, string>> = {
  organizer: 'Organizador',
  required: 'Obligatorio',
  optional: 'Opcional',
};

export const VIDEO_PROVIDER_LABELS: Readonly<Record<string, string>> = {
  google: 'Google Meet',
  google_meet: 'Google Meet',
  meet: 'Google Meet',
  microsoft: 'Microsoft Teams',
  teams: 'Microsoft Teams',
  zoom: 'Zoom',
  jitsi: 'Jitsi',
  custom: 'Enlace personalizado',
  none: 'Sin videollamada',
};

export const SCHEDULING_MODE_LABELS: Readonly<Record<string, string>> = {
  one_on_one: 'Uno a uno',
  round_robin: 'Rotativo',
  round_robin_weighted: 'Rotativo ponderado',
  collective: 'Colectivo',
  group_event: 'Evento grupal',
};

export const ROUTING_RULE_TYPE_LABELS: Readonly<Record<string, string>> = {
  contact_owner: 'Responsable del contacto',
  territory: 'Territorio',
  company_size: 'Tamaño de empresa',
  industry: 'Sector',
  least_busy: 'Menos ocupado',
  custom: 'Personalizada',
};

export const FORM_FIELD_TYPE_LABELS: Readonly<Record<string, string>> = {
  text: 'Texto',
  textarea: 'Texto largo',
  email: 'Correo',
  phone: 'Teléfono',
  dropdown: 'Lista desplegable',
  checkbox: 'Casillas',
  radio: 'Opción única',
  hidden: 'Oculto',
  number: 'Número',
};

export const RESCHEDULE_ACTION_LABELS: Readonly<Record<string, string>> = {
  reschedule: 'Reprogramación',
  cancel: 'Cancelación',
  no_show: 'Inasistencia',
};

export const RESCHEDULE_INITIATED_BY_LABELS: Readonly<Record<string, string>> = {
  invitee: 'Invitado',
  host: 'Anfitrión',
  system: 'Sistema',
};

export const REMINDER_STATUS_LABELS: Readonly<Record<string, string>> = {
  PENDING: 'Pendiente',
  SENT: 'Enviado',
  FAILED: 'Falló',
};

export const VIDEO_SYNC_ERROR_LABELS: Readonly<Record<string, string>> = {
  ACCOUNT_NOT_CONNECTED: 'La cuenta de video no está conectada',
  API_DISABLED_OR_FORBIDDEN: 'La API del proveedor está deshabilitada o sin permisos',
  AUTH_EXPIRED: 'La autorización con el proveedor expiró',
  PROVIDER_ERROR: 'El proveedor devolvió un error',
  UNKNOWN: 'Error desconocido al generar el enlace',
};

export type FormFieldType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'dropdown'
  | 'checkbox'
  | 'radio'
  | 'hidden'
  | 'number';

export type RoutingRuleType =
  | 'contact_owner'
  | 'territory'
  | 'company_size'
  | 'industry'
  | 'least_busy'
  | 'custom';

export type ActionTokenType = 'cancel' | 'reschedule' | 'confirm';

export type OAuthProvider = 'google' | 'microsoft' | 'zoom' | 'salesforce';

export type RescheduleAction = 'reschedule' | 'cancel' | 'no_show';
export type RescheduleInitiatedBy = 'invitee' | 'host' | 'system';

export type AuditAction =
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'ACTIVATE'
  | 'DEACTIVATE';

/**
 * INC 71 — causa estructurada del fallo al generar el enlace de video (Google/Microsoft).
 * La reunión SIEMPRE se guarda; este código solo describe por qué quedó sin enlace.
 * null = sin fallo (enlace generado o proveedor sin auto-link).
 */
export type VideoSyncErrorCode =
  | 'ACCOUNT_NOT_CONNECTED'
  | 'API_DISABLED_OR_FORBIDDEN'
  | 'AUTH_EXPIRED'
  | 'PROVIDER_ERROR'
  | 'UNKNOWN';

// -------------------------------------------------------------------
// Pagination — soporta ambos shapes (backend nuevo y legacy)
// -------------------------------------------------------------------

export interface PageResponse<T = unknown> {
  content: T[];
  // Nuevo shape (APIBACKNEW)
  page?: number;
  size: number;
  totalElements: number;
  totalPages: number;
  hasNext?: boolean;
  hasPrevious?: boolean;
  // Legacy Spring Data shape
  number?: number;
  first?: boolean;
  last?: boolean;
  empty?: boolean;
}

// LocalTime — Spring serializa como objeto {hour,minute,second,nano}
// pero acepta "HH:mm:ss" como string en la mayoría de endpoints.
export type LocalTimeString = string; // "HH:mm:ss"
export interface LocalTimeObject {
  hour: number;
  minute: number;
  second: number;
  nano: number;
}
export type LocalTime = LocalTimeString | LocalTimeObject;

// -------------------------------------------------------------------
// 1. Meetings
// -------------------------------------------------------------------

export interface MeetingRequest {
  // Requeridos
  title: string;
  datetimeStart: string; // ISO UTC
  datetimeEnd?: string;  // ISO UTC — opcional: se deriva de idMeetingType/idBookingLink si va vacío
  businessId: number;

  // CRM
  description?: string;
  location?: string;
  meetingUrl?: string;
  locationMapUrl?: string;
  idMeetingType?: number;
  idMeetingStatus?: number;
  dealId?: number;
  companyId?: number;
  contactId?: number;
  organizerId?: number;
  notes?: string;
  outcome?: string;

  // B13 — booking / payment / GDPR / routing
  cancellationReasonText?: string;
  idCancellationReason?: number;
  source?: MeetingSource | string;
  isNoShow?: boolean;
  noShowDetectedAt?: string;
  paymentStatus?: MeetingPaymentStatus | string;
  paymentAmount?: number;
  paymentIntentId?: string;
  videoProvider?: string;
  videoJoinUrl?: string;
  /**
   * Token de la sala de video (Jitsi). READ-ONLY: lo genera el backend cuando
   * videoProvider='jitsi' y se REUTILIZA en edición. No enviarlo en create/update.
   * El roomName de Jitsi se deriva como "partnercrm-"+videoRoomToken.
   */
  videoRoomToken?: string;
  inviteeTimezone?: string;
  gdprConsentGiven?: boolean;
  gdprConsentDatetime?: string;
  userAssignedByRoutingId?: number;
  routingRuleAppliedId?: number;

  // Integración booking pages
  idBookingLink?: number;
  timezone?: string;
  preQualificationData?: string; // JSON string
  externalEventId?: string;

  /**
   * INC 41 — IDs de usuarios internos invitados a la reunión (NO incluir organizerId).
   * CREATE: agrega y notifica a todos. UPDATE: reconcilia (null/omitir = no tocar; [] = quitar todos).
   */
  internalParticipantIds?: number[];
}

export interface MeetingResponse extends MeetingRequest {
  id: number;
  rescheduleCount?: number;
  datetimeCreated?: string;
  datetimeUpdated?: string;
  /**
   * READ-ONLY. Mensaje (en español) si el backend no pudo generar el enlace de
   * videollamada al sincronizar con el calendario externo (Google/Microsoft).
   * La reunión se guarda igual; este campo solo informa que el enlace quedó
   * pendiente. Null cuando la sincronización fue correcta o no aplica.
   */
  videoSyncError?: string;
  /**
   * INC 71 — READ-ONLY. Código estructurado del motivo del fallo del enlace de
   * video, para mostrar un mensaje accionable en la UI. Null cuando no hubo fallo.
   */
  videoSyncErrorCode?: VideoSyncErrorCode | null;
  /**
   * READ-ONLY y TRANSITORIO. `true` cuando el correo de confirmación al cliente
   * NO se envió porque la reunión quedó sin enlace de videollamada y sin lugar
   * presencial. La reunión SÍ se guardó (201/200 normal). Nunca llega `false`.
   *
   * Solo viene poblado en la respuesta de CREAR (POST) y EDITAR (PUT); en GET y
   * en los listados llega `null`.
   */
  confirmationEmailSkipped?: boolean | null;
  /**
   * READ-ONLY y TRANSITORIO. Motivo legible (español) por el que se omitió el
   * correo de confirmación. Solo viene cuando `confirmationEmailSkipped === true`,
   * y solo en la respuesta de crear/editar (en GET y listados llega `null`).
   */
  confirmationEmailSkippedReason?: string | null;
}

/** @deprecated usar MeetingResponse para respuestas y MeetingRequest para mutaciones */
export interface Meeting extends MeetingResponse {}

export interface MeetingSearchParams {
  businessId: number;
  idMeetingType?: number;
  idMeetingStatus?: number;
  dealId?: number;
  companyId?: number;
  contactId?: number;
  organizerId?: number;
  datetimeStartFrom?: string;
  datetimeStartTo?: string;
  searchTitle?: string;
  upcomingOnly?: boolean;
  pastOnly?: boolean;
  isNoShow?: boolean;
  paymentStatus?: string;
  source?: string;

  /**
   * INC 74 — alcance del listado: 'MINE' = reuniones donde el usuario del JWT es
   * organizador o participante; 'ALL' = todas del negocio. Default backend: ALL.
   */
  scope?: 'MINE' | 'ALL';

  // Aliases legacy — mantener para compatibilidad con código existente
  dateFrom?: string;
  dateTo?: string;
  searchTerm?: string;

  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc' | 'ASC' | 'DESC';
}

// Sub-recursos de Meeting -------------------------------------------

export interface MeetingParticipantRequest {
  idMeeting: number;
  contactId?: number;
  userId?: number;
  participantType?: ParticipantType;
  status?: ParticipantStatus;
  responseDatetime?: string;
  notes?: string;
  joinedAt?: string;
  leftAt?: string;
  isNoShow?: boolean;
  email?: string;
  name?: string;
  phone?: string;
}
export interface MeetingParticipantResponse extends MeetingParticipantRequest {
  id: number;
  /**
   * INC 44 — rol legible (en español) ya denormalizado por el backend a partir de
   * participantType: 'Organizador' | 'Invitado interno' | 'Contacto externo'.
   */
  role?: string;
  datetimeCreated?: string;
  datetimeUpdated?: string;
}

export interface MeetingAgendaItemRequest {
  idMeeting: number;
  title: string;
  order?: number;
  description?: string;
  durationMinutes?: number;
  presenter?: string;
}
export interface MeetingAgendaItemResponse extends MeetingAgendaItemRequest {
  id: number;
}

/** INC 73 — estado real de envío del recordatorio. */
export type ReminderStatus = 'PENDING' | 'SENT' | 'FAILED';

export interface MeetingReminderRequest {
  idMeeting: number;
  reminderMinutesBefore: number;
  reminderMethod?: ReminderMethod;
  /** INC 46 — a quién se le envía. Default backend: 'organizer'. */
  reminderTarget?: ReminderTarget;
  /**
   * INC 72 — plantilla de correo a usar en el recordatorio. Solo aplica cuando
   * reminderMethod es 'email' o 'all'. null = usar la plantilla por defecto.
   */
  templateId?: number | null;
}
export interface MeetingReminderResponse extends MeetingReminderRequest {
  id: number;
  reminderSent?: boolean;
  datetimeSent?: string;
  datetimeCreated?: string;
  /** INC 73 — estado real: PENDING | SENT | FAILED. */
  status?: ReminderStatus;
  /** INC 73 — motivo del fallo cuando status === 'FAILED'. */
  failureReason?: string | null;
}

export interface MeetingRescheduleLogRequest {
  idMeeting: number;
  action: RescheduleAction;
  originalDatetimeStart: string;
  originalDatetimeEnd: string;
  newDatetimeStart?: string;
  newDatetimeEnd?: string;
  idCancellationReason?: number;
  reasonText?: string;
  initiatedBy?: RescheduleInitiatedBy;
  userInitiatedId?: number;
  rescheduleNumber?: number;
}
export interface MeetingRescheduleLogResponse extends MeetingRescheduleLogRequest {
  id: number;
  datetimeCreated?: string;
}

// -------------------------------------------------------------------
// 2. Meeting Types
// -------------------------------------------------------------------

export interface MeetingTypeRequest {
  businessId: number;
  name: string;
  label: string;
  description?: string;
  icon?: string;
  color?: string;
  requiresLocation?: boolean;
  requiresUrl?: boolean;
  defaultDurationMinutes?: number;
  sortOrder?: number;
  isActive?: boolean;
}
export interface MeetingTypeResponse extends MeetingTypeRequest {
  id: number;
  datetimeCreated?: string;
  datetimeUpdated?: string;
  // legacy
  duration?: number;
  order?: number;
}
/** @deprecated alias legacy */
export interface MeetingType extends MeetingTypeResponse {}

// Meeting Type Availability (franjas horarias permitidas por tipo) — INC 35
export interface MeetingTypeAvailabilityRequest {
  businessId: number;
  dayOfWeek: number;     // 0=domingo .. 6=sábado
  startTime: string;     // "HH:mm"
  endTime: string;       // "HH:mm"
}
export interface MeetingTypeAvailabilityResponse {
  id: number;
  idMeetingType: number;
  businessId: number;
  dayOfWeek: number;
  startTime: string;     // "HH:mm:ss"
  endTime: string;       // "HH:mm:ss"
  datetimeCreated?: string;
  datetimeUpdated?: string;
}

// -------------------------------------------------------------------
// 3. Meeting Statuses
// -------------------------------------------------------------------

export interface MeetingStatusRequest {
  businessId: number;
  name: string;
  label: string;
  description?: string;
  color?: string;
  sortOrder?: number;
  isFinal?: boolean;
  isDefault?: boolean;
  isSystem?: boolean;
  isActive?: boolean;
}
export interface MeetingStatusResponse extends MeetingStatusRequest {
  id: number;
  datetimeCreated?: string;
  datetimeUpdated?: string;
  // legacy
  order?: number;
}
/** @deprecated alias legacy */
export interface MeetingStatus extends MeetingStatusResponse {}

// -------------------------------------------------------------------
// 4. Meeting Cancellation Reasons
// -------------------------------------------------------------------

export interface MeetingCancellationReasonRequest {
  businessId: number;
  name: string;
  sortOrder?: number;
  isActive?: boolean;
}
export interface MeetingCancellationReasonResponse
  extends MeetingCancellationReasonRequest {
  id: number;
  datetimeCreated?: string;
}

// -------------------------------------------------------------------
// 5. Meeting Email Templates
// -------------------------------------------------------------------

export interface MeetingEmailTemplateRequest {
  businessId: number;
  templateType: EmailTemplateType | string;
  name: string;
  subject: string;
  bodyHtml: string;
  bodyText?: string;
  idBookingLink?: number;
  isDefault?: boolean;
  isActive?: boolean;
}
export interface MeetingEmailTemplateResponse
  extends MeetingEmailTemplateRequest {
  id: number;
  datetimeCreated?: string;
  datetimeUpdated?: string;
}

/**
 * INC 81 — plantilla PREDETERMINADA DEL SISTEMA (solo lectura).
 *
 * Es la plantilla de plataforma que se envía cuando el negocio no ha marcado ninguna propia
 * como predeterminada. Sin `id` a propósito: no vive en `meeting_email_template` y no se
 * puede editar ni eliminar.
 */
export interface MeetingSystemEmailTemplateResponse {
  templateType: EmailTemplateType | string;
  name: string;
  purpose: string;
  subject?: string;
  bodyHtml?: string;
  bodyText?: string;
  isSystemDefault: boolean;
  editable: boolean;
}

// -------------------------------------------------------------------
// 6. Meeting Holidays
// -------------------------------------------------------------------

export interface MeetingHolidayRequest {
  businessId: number;
  holidayDate: string; // YYYY-MM-DD
  name: string;
  countryCode?: string;
  isRecurring?: boolean;
  isActive?: boolean;
}
export interface MeetingHolidayResponse extends MeetingHolidayRequest {
  id: number;
  datetimeCreated?: string;
}

// -------------------------------------------------------------------
// 7. Booking Pages (meeting-books-links)
// -------------------------------------------------------------------

export interface MeetingBooksLinkRequest {
  // Requeridos
  idBussines: number; // sic: typo oficial del backend
  idUser: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  minNoticeMinutes: number;

  slug?: string | null;
  idType?: number;
  isActive?: boolean;

  title?: string | null;
  description?: string | null;
  schedulingMode?: SchedulingMode | string;

  // Branding
  logoUrl?: string | null;
  brandColor?: string | null;
  darkMode?: boolean;
  hideBranding?: boolean;

  // Idioma
  defaultLanguage?: string;
  supportedLanguages?: string | null; // JSON array string

  // Slots
  maxAdvanceDays?: number;
  maxMeetingsPerDay?: number;
  slotIntervalMinutes?: number;
  defaultDurationMinutes?: number;
  timezone?: string | null;

  // Video
  videoProvider?: string | null;
  customVideoUrl?: string | null;
  phoneNumber?: string | null;

  // Presencial
  physicalAddress?: string | null;
  physicalAddressUrl?: string | null;
  roomName?: string | null;

  // Pago
  requiresPayment?: boolean;
  paymentAmount?: number;
  paymentCurrency?: string;
  stripePriceId?: string | null;

  // Reschedule
  minCancelNoticeMinutes?: number;
  maxReschedules?: number;

  // Grupo
  maxInviteesPerSlot?: number;

  // Confirmación
  confirmationEmailEnabled?: boolean;
  followupEmailEnabled?: boolean;
  followupDelayMinutes?: number;
  noShowWaitMinutes?: number;

  // Waiting room
  waitingRoomEnabled?: boolean;
  waitingRoomMessage?: string | null;

  // Agenda default
  defaultAgenda?: string | null; // JSON string

  // GDPR
  gdprConsentEnabled?: boolean;
  gdprConsentText?: string | null;
  gdprPrivacyUrl?: string | null;

  // Embed
  embedType?: EmbedType | string;

  // Pipeline
  autoUpdatePipelineStage?: boolean;
  idPipelineStageOnBook?: number;

  // Lead scoring
  leadScoreIncrement?: number;
}

export interface MeetingBooksLinkResponse extends MeetingBooksLinkRequest {
  id: number;
  fullUrl?: string;
  datetimeCreated?: string;
  datetimeUpdated?: string;
}

// Sub-recursos booking link ----------------------------------------

export interface MeetingBookingAvailabilityRequest {
  dayOfWeek: number; // 0-6
  startTime: LocalTime;
  endTime: LocalTime;
  isActive?: boolean;
}
export interface MeetingBookingAvailabilityResponse
  extends MeetingBookingAvailabilityRequest {
  id: number;
  idBookingLink: number;
  datetimeCreated?: string;
}

export interface MeetingBookingDurationRequest {
  durationMinutes: number;
  label?: string;
  price?: number;
  isDefault?: boolean;
  sortOrder?: number;
  isActive?: boolean;
}
export interface MeetingBookingDurationResponse
  extends MeetingBookingDurationRequest {
  id: number;
  idBookingLink: number;
  datetimeCreated?: string;
}

// INC 38 — operadores soportados por la lógica condicional de visibilidad.
export type ConditionalLogicOperator = 'equals' | 'notEquals' | 'contains';

/**
 * INC 38 — regla de visibilidad de un campo: "mostrar este campo solo si OTRO campo
 * (`field`) cumple `operator` respecto a `value`". Se persiste serializada con
 * JSON.stringify en el campo `conditionalLogic` (jsonb en el backend).
 */
export interface ConditionalLogicRule {
  field: string;
  operator: ConditionalLogicOperator;
  value: string;
}

export interface MeetingBookingFormFieldRequest {
  fieldName: string;
  fieldLabel: string;
  fieldType: FormFieldType | string;
  isRequired?: boolean;
  sortOrder?: number;
  options?: string; // JSON string
  placeholder?: string;
  defaultValue?: string;
  crmProperty?: string;
  conditionalLogic?: string; // JSON string — ConditionalLogicRule serializada (INC 38)
  isSystem?: boolean;
  isActive?: boolean;
}
export interface MeetingBookingFormFieldResponse
  extends MeetingBookingFormFieldRequest {
  id: number;
  idBookingLink: number;
  datetimeCreated?: string;
}

export interface MeetingBookingMemberRequest {
  idUser: number;
  priorityWeight?: number;
  isActive?: boolean;
}
export interface MeetingBookingMemberResponse
  extends MeetingBookingMemberRequest {
  id: number;
  idBookingLink: number;
  datetimeCreated?: string;
}

export interface MeetingNurtureEmailRequest {
  delayHours: number;
  subject: string;
  bodyHtml: string;
  bodyText?: string;
  sortOrder?: number;
  isActive?: boolean;
}
export interface MeetingNurtureEmailResponse
  extends MeetingNurtureEmailRequest {
  id: number;
  idBookingLink: number;
  datetimeCreated?: string;
}

export interface MeetingReminderConfigRequest {
  minutesBefore: number;
  method: BookingReminderMethod;
  target: BookingReminderTarget;
  idEmailTemplate?: number;
  isActive?: boolean;
}
export interface MeetingReminderConfigResponse
  extends MeetingReminderConfigRequest {
  id: number;
  idBookingLink: number;
  datetimeCreated?: string;
}

export interface MeetingRoundRobinStateResponse {
  id: number;
  idBookingLink: number;
  idUserLast?: number;
  meetingsAssigned?: number;
  lastAssignedAt?: string;
}

export interface BookingLinkFilterParams {
  userId?: number;
  businessId?: number;
  typeId?: number;
  isActive?: boolean;
}

// -------------------------------------------------------------------
// 8. User Availability
// -------------------------------------------------------------------

export interface MeetingUserAvailabilityRequest {
  idUser: number;
  idBussines: number; // sic
  dayOfWeek: number;
  startTime: LocalTime;
  endTime: LocalTime;
  timezone: string;
  isWorkingDay: boolean;
}
export interface MeetingUserAvailabilityResponse
  extends MeetingUserAvailabilityRequest {
  id: number;
  dayOfWeekName?: string;
  datetimeCreated?: string;
}

// Alias legacy usado por componentes existentes
export interface AvailabilityApi {
  id?: number;
  idUser?: number;
  idBussines?: number;
  dayOfWeek: number;
  dayOfWeekName?: string;
  startTime: string;
  endTime: string;
  timezone?: string;
  isWorkingDay: boolean;
  datetimeCreated?: string;
}

export interface CheckAvailabilityParams {
  dayOfWeek: number;
  time: string; // ISO date-time
}

// -------------------------------------------------------------------
// 9. Availability Overrides
// -------------------------------------------------------------------

export interface MeetingUserAvailabilityOverrideRequest {
  idUser: number;
  businessId: number;
  overrideDate: string; // YYYY-MM-DD
  isAvailable?: boolean;
  startTime?: LocalTime;
  endTime?: LocalTime;
  reason?: string;
}
export interface MeetingUserAvailabilityOverrideResponse
  extends MeetingUserAvailabilityOverrideRequest {
  id: number;
  datetimeCreated?: string;
}

// -------------------------------------------------------------------
// 10. OAuth Integrations
// -------------------------------------------------------------------

export interface MeetingIntegrationsOauthRequest {
  idBussiness: number; // sic (diferente al de BooksLink: aquí con 'ss')
  idUser: number;
  provider: OAuthProvider;
  accessToken: string;
  refreshToken?: string;
  expiresAt?: string;
  active?: boolean;
}
export interface MeetingIntegrationsOauthResponse
  extends MeetingIntegrationsOauthRequest {
  id: number;
  createdAt?: string;
}

// -------------------------------------------------------------------
// 11. Routing Rules
// -------------------------------------------------------------------

export interface MeetingRoutingRuleRequest {
  businessId: number;
  ruleName: string;
  ruleType: RoutingRuleType | string;
  ruleConfig: string; // JSON string
  idBookingLink?: number;
  priorityOrder?: number;
  userTargetId?: number;
  idBookingLinkFallback?: number;
  isActive?: boolean;
}
export interface MeetingRoutingRuleResponse extends MeetingRoutingRuleRequest {
  id: number;
  datetimeCreated?: string;
  datetimeUpdated?: string;
}

// -------------------------------------------------------------------
// 12. Action Tokens
// -------------------------------------------------------------------

export interface MeetingActionTokenRequest {
  idMeeting: number;
  actionType: ActionTokenType;
  expiresAt: string;
}
export interface MeetingActionTokenResponse {
  id: number;
  idMeeting: number;
  token: string;
  actionType: string;
  expiresAt: string;
  usedAt?: string;
  isUsed?: boolean;
  datetimeCreated?: string;
}

// -------------------------------------------------------------------
// 13. Audit Logs (read-only)
// -------------------------------------------------------------------

/**
 * INC 47 — cambio campo-por-campo ya legible (valores resueltos a nombre por el
 * backend). Compatible con `FieldChangeDisplay` que consume <app-audit-timeline>.
 */
export interface MeetingAuditFieldChange {
  field: string;
  label: string;
  oldValue?: string | null;
  newValue?: string | null;
}

export interface MeetingAuditLogResponse {
  id: number;
  businessId: number;
  userId?: number;
  /** INC 47 — nombre completo del usuario, denormalizado por el backend. */
  userFullName?: string;
  entityType: string;
  entityId: number;
  action: AuditAction | string;
  /** INC 47 — etiqueta legible (en español) de la acción, denormalizada por el backend. */
  actionLabel?: string;
  changes?: string; // JSON string crudo
  /** INC 47 — descripción legible de los cambios; null en filas antiguas → fallback a `changes`. */
  changesDescription?: string;
  /**
   * INC 47 — diff campo-por-campo (valores ya legibles). `[]` en filas "created"
   * o legadas → usar `changesDescription` como fallback.
   */
  fieldChanges?: MeetingAuditFieldChange[];
  ipAddress?: string;
  datetimeCreated?: string;
}

// -------------------------------------------------------------------
// Public booking (página pública /book/:slug — sin JWT)
// -------------------------------------------------------------------

export interface PublicBookingRequest {
  datetimeStart: string; // ISO con offset
  durationMinutes: number;
  inviteeName: string;
  inviteeEmail: string;
  inviteePhone?: string;
  inviteeTimezone?: string;
  preQualificationData?: string; // JSON string
}

/**
 * INC 80 — sin `videoJoinUrl` a propósito: la pantalla de confirmación mostraba
 * "Unirse a la videollamada" nada más reservar. El backend ya no lo devuelve.
 */
export interface PublicBookingResponse {
  meetingId: number;
  title: string;
  datetimeStart: string;
  datetimeEnd: string;
  timezone?: string;
}

// -------------------------------------------------------------------
// INC 79 — enlaces públicos de cancelar / reprogramar (/#/meeting-action/:token)
// GET|POST /api/public/meeting-action/{token}[/cancel|/reschedule] — sin JWT
// -------------------------------------------------------------------

/** Vista pública de la reunión + qué acciones permite el enlace. */
export interface PublicMeetingActionResponse {
  actionType: string | null;
  expiresAt?: string | null;
  title: string;
  datetimeStart: string;
  datetimeEnd?: string | null;
  timezone?: string | null;
  location?: string | null;
  joinUrl?: string | null;
  organizerName?: string | null;
  businessName?: string | null;
  cancelled: boolean;
  canCancel: boolean;
  canReschedule: boolean;
  /** Motivo por el que la acción no está disponible (plazo, límite, ya cancelada…). */
  blockedReason?: string | null;
  minCancelNoticeMinutes?: number | null;
  maxReschedules?: number | null;
  rescheduleCount?: number | null;
}

export interface PublicMeetingCancelRequest {
  reasonText?: string | null;
  idCancellationReason?: number | null;
}

export interface PublicMeetingRescheduleRequest {
  datetimeStart: string;
  datetimeEnd?: string | null;
  reasonText?: string | null;
}

/**
 * INC 76 — franja ocupada del organizador (GET /api/public/booking/{slug}/busy).
 * Ya viene expandida con el buffer de la página, así que la página pública solo
 * descarta los horarios que se solapen. No expone datos de la reunión existente.
 */
export interface PublicBusyInterval {
  start: string;
  end: string;
}

// -------------------------------------------------------------------
// Public meeting room (videollamada Jitsi — GET /api/public/meeting-room/{token}, sin JWT)
// -------------------------------------------------------------------

export interface PublicMeetingRoomResponse {
  title: string;
  datetimeStart: string | null; // ISO UTC, puede ser null
  datetimeEnd: string | null;   // ISO UTC, puede ser null
  durationMinutes: number | null;
  roomName: string;             // "partnercrm-"+token
  organizerName: string | null;
  status: string | null;
}

// -------------------------------------------------------------------
// UI helpers (kept for existing components)
// -------------------------------------------------------------------

export interface TimeSlotUI {
  start: string;
  end: string;
}

export interface DayAvailabilityUI {
  enabled: boolean;
  slots: TimeSlotUI[];
  id?: number;
}

export interface WeekAvailabilityUI {
  [key: string]: DayAvailabilityUI;
}
